import Pusher from 'pusher-js/with-encryption';
import { baseURL } from 'api/forethoughtFetch';
import { getOrganizationId } from 'components/salesforce-messaging-one-chat/dataProvider';
import { getStore } from 'storeRef';
import {
  selectApiKey,
  selectIsInteractiveEmailPreview,
  selectIsDraft,
  selectOrigin,
} from 'reducers/config';
import {
  selectIsBuilderPreview,
  selectIsSolveConfigPreview,
  selectWorkflowVersion,
} from 'reducers/workflows';
import { buildHeaders } from 'utils/buildHeaders';

interface ChannelAuthorizationData {
  auth: string;
  channel_data?: string;
  shared_secret?: string;
}

// Global Pusher instance shared across the application
let globalPusher: Pusher | null = null;

const createPusherAuthHandler = (endpoint: string) => {
  return async (
    params: { channelName: string; socketId: string },
    callback: (
      error: Error | null,
      authData: ChannelAuthorizationData | null,
    ) => void,
  ) => {
    try {
      const store = getStore();
      const state = store.getState();

      // Get all required values from state
      const apiKey = selectApiKey(state);
      const origin = selectOrigin(state);
      const isBuilderPreview = selectIsBuilderPreview(state);
      const isSolveConfigPreview = selectIsSolveConfigPreview(state);
      const workflowVersion = selectWorkflowVersion(state);
      const isDraft = selectIsDraft(state);
      const isInteractiveEmailPreview = selectIsInteractiveEmailPreview(state);

      const apiHeaders = buildHeaders(
        apiKey,
        origin,
        isBuilderPreview,
        isSolveConfigPreview,
        workflowVersion,
        isDraft,
        isInteractiveEmailPreview,
      );

      // Use all existing headers from buildHeaders
      const headers: Record<string, string> = {
        Authorization: apiHeaders.Authorization,
        'Content-Type': 'application/json',
        'is-config-preview': String(apiHeaders['is-config-preview']),
        'is-draft': String(apiHeaders['is-draft']),
        'is-preview': String(apiHeaders['is-preview']),
        'solve-origin': apiHeaders['solve-origin'],
        'X-ORG-ID': getOrganizationId(),
      };

      // optional
      if (apiHeaders['workflow-config-version']) {
        headers['workflow-config-version'] =
          apiHeaders['workflow-config-version'];
      }

      const response = await fetch(endpoint, {
        body: JSON.stringify({
          channel_name: params.channelName,
          socket_id: params.socketId,
        }),
        headers,
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Pusher authorization failed: ${response.status}`);
      }

      const authData = (await response.json()) as ChannelAuthorizationData;
      callback(null, authData);
    } catch (error) {
      console.error('Error authorizing Pusher channel:', error);
      callback(error as Error, null);
    }
  };
};

/**
 * Gets or creates the global Pusher instance
 */
export const getOrCreatePusher = (): Pusher => {
  if (!globalPusher) {
    const PUSHER_KEY =
      process.env.REACT_APP_PUSHER_KEY || '7d93496a6cb894633b32';
    const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'us3';
    const endpoint = `${baseURL}/pusher/solve/auth`;

    globalPusher = new Pusher(PUSHER_KEY, {
      channelAuthorization: {
        customHandler: createPusherAuthHandler(endpoint),
        endpoint,
        transport: 'ajax',
      },
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
    });
  }
  return globalPusher;
};
